import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { User } from '@supabase/supabase-js';
import { tasks as initialTasks, type Task } from '@/mocks/tasks';
import { projects as initialProjects, type Project } from '@/mocks/projects';
import { isSupabaseConfigured, supabase } from '@/lib/supabase';

interface AppState {
  tasks: Task[];
  projects: Project[];
  toggleTask: (id: string) => void;
  addTask: (task: Omit<Task, 'id' | 'completed' | 'createdAt'>) => void;
  editTask: (id: string, updates: Partial<Task>) => void;
  postponeTask: (id: string) => void;
  deleteTask: (id: string) => void;
  todayTasks: Task[];
  upcomingTasks: Task[];
  todayImportantTasks: Task[];
  completedTasks: Task[];
  completedTodayCount: number;
  darkMode: boolean;
  toggleDarkMode: () => void;
  shareModalOpen: boolean;
  shareModalTarget: string | null;
  openShareModal: (id: string) => void;
  closeShareModal: () => void;
  toastMessage: string | null;
  showToast: (msg: string) => void;
  clearToast: () => void;
  currentUser: User | null;
  cloudSyncEnabled: boolean;
  sendSignInLink: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AppContext = createContext<AppState | null>(null);

const priorityToDb: Record<Task['priority'], 'high' | 'medium' | 'low'> = { 高: 'high', 中: 'medium', 低: 'low' };
const priorityFromDb: Record<string, Task['priority']> = { high: '高', medium: '中', low: '低' };

function toDueAt(date: string, time: string) {
  return new Date(`${date}T${time || '09:00'}:00`).toISOString();
}

function toDateAndTime(value: string | null) {
  if (!value) return { dueDate: '', dueTime: '' };
  const date = new Date(value);
  return { dueDate: date.toISOString().slice(0, 10), dueTime: date.toISOString().slice(11, 16) };
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [tasks, setTasks] = useState<Task[]>(initialTasks);
  const [projects, setProjects] = useState<Project[]>(initialProjects);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [darkMode, setDarkMode] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareModalTarget, setShareModalTarget] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = useCallback((msg: string) => setToastMessage(msg), []);
  const clearToast = useCallback(() => setToastMessage(null), []);

  const loadCloudData = useCallback(async (userId: string) => {
    if (!supabase) return;
    const [{ data: projectRows, error: projectError }, { data: taskRows, error: taskError }] = await Promise.all([
      supabase.from('projects').select('*').eq('user_id', userId).order('created_at'),
      supabase.from('tasks').select('*').eq('user_id', userId).order('due_at'),
    ]);
    if (projectError || taskError) {
      showToast('云端数据读取失败，暂时保留本地演示数据');
      return;
    }
    const projectList: Project[] = (projectRows ?? []).map((project) => ({
      id: project.id,
      name: project.name,
      goal: project.goal ?? '',
      stage: project.stage ?? '规划',
      progress: project.progress ?? 0,
      nextStep: project.next_step ?? '',
      deadline: project.deadline ?? '',
      status: project.status,
      taskCount: (taskRows ?? []).filter((task) => task.project_id === project.id && !task.completed).length,
      updates: [],
    }));
    const projectNames = new Map(projectList.map((project) => [project.id, project.name]));
    const taskList: Task[] = (taskRows ?? []).map((task) => ({
      id: task.id,
      title: task.title,
      priority: priorityFromDb[task.priority] ?? '中',
      projectId: task.project_id ?? '',
      projectName: projectNames.get(task.project_id) ?? '',
      ...toDateAndTime(task.due_at),
      completed: task.completed,
      createdAt: task.created_at,
    }));
    setProjects(projectList);
    setTasks(taskList);
  }, [showToast]);

  useEffect(() => {
    if (!supabase) return;
    const initialize = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setCurrentUser(session?.user ?? null);
      if (session?.user) await loadCloudData(session.user.id);
    };
    void initialize();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setCurrentUser(session?.user ?? null);
      if (session?.user) void loadCloudData(session.user.id);
    });
    return () => subscription.unsubscribe();
  }, [loadCloudData]);

  useEffect(() => {
    if (!supabase || !currentUser) return;
    const channel = supabase
      .channel(`personal-data-${currentUser.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks', filter: `user_id=eq.${currentUser.id}` }, () => void loadCloudData(currentUser.id))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'projects', filter: `user_id=eq.${currentUser.id}` }, () => void loadCloudData(currentUser.id))
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [currentUser, loadCloudData]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
  }, [darkMode]);

  const toggleDarkMode = useCallback(() => setDarkMode((prev) => !prev), []);
  const openShareModal = useCallback((id: string) => { setShareModalTarget(id); setShareModalOpen(true); }, []);
  const closeShareModal = useCallback(() => { setShareModalOpen(false); setShareModalTarget(null); }, []);

  const saveTask = useCallback(async (id: string, updates: Partial<Task>) => {
    if (!supabase || !currentUser) return;
    const payload: Record<string, unknown> = {};
    if (updates.title !== undefined) payload.title = updates.title;
    if (updates.priority !== undefined) payload.priority = priorityToDb[updates.priority];
    if (updates.projectId !== undefined) payload.project_id = updates.projectId || null;
    if (updates.dueDate !== undefined || updates.dueTime !== undefined) {
      const task = tasks.find((item) => item.id === id);
      const dueDate = updates.dueDate ?? task?.dueDate ?? '';
      const dueTime = updates.dueTime ?? task?.dueTime ?? '';
      payload.due_at = dueDate ? toDueAt(dueDate, dueTime) : null;
    }
    if (updates.completed !== undefined) {
      payload.completed = updates.completed;
      payload.completed_at = updates.completed ? new Date().toISOString() : null;
    }
    const { error } = await supabase.from('tasks').update(payload).eq('id', id).eq('user_id', currentUser.id);
    if (error) showToast('云端同步失败，请稍后重试');
  }, [currentUser, showToast, tasks]);

  const toggleTask = useCallback((id: string) => {
    const task = tasks.find((item) => item.id === id);
    if (!task) return;
    const completed = !task.completed;
    setTasks((prev) => prev.map((item) => item.id === id ? { ...item, completed } : item));
    void saveTask(id, { completed });
  }, [saveTask, tasks]);

  const addTask = useCallback((taskData: Omit<Task, 'id' | 'completed' | 'createdAt'>) => {
    const newTask: Task = { ...taskData, id: `t${Date.now()}`, completed: false, createdAt: new Date().toISOString() };
    setTasks((prev) => [newTask, ...prev]);
    if (!supabase || !currentUser) return;
    void supabase.from('tasks').insert({
      user_id: currentUser.id,
      title: taskData.title,
      priority: priorityToDb[taskData.priority],
      project_id: taskData.projectId || null,
      due_at: taskData.dueDate ? toDueAt(taskData.dueDate, taskData.dueTime) : null,
    }).then(({ error }) => {
      if (error) showToast('云端同步失败，请稍后重试');
      else void loadCloudData(currentUser.id);
    });
  }, [currentUser, loadCloudData, showToast]);

  const editTask = useCallback((id: string, updates: Partial<Task>) => {
    setTasks((prev) => prev.map((task) => task.id === id ? { ...task, ...updates } : task));
    void saveTask(id, updates);
  }, [saveTask]);

  const postponeTask = useCallback((id: string) => {
    const task = tasks.find((item) => item.id === id);
    if (!task?.dueDate) return;
    const next = new Date(`${task.dueDate}T00:00:00`);
    next.setDate(next.getDate() + 1);
    const dueDate = next.toISOString().slice(0, 10);
    setTasks((prev) => prev.map((item) => item.id === id ? { ...item, dueDate } : item));
    void saveTask(id, { dueDate });
  }, [saveTask, tasks]);

  const deleteTask = useCallback((id: string) => {
    setTasks((prev) => prev.filter((task) => task.id !== id));
    if (!supabase || !currentUser) return;
    void supabase.from('tasks').delete().eq('id', id).eq('user_id', currentUser.id).then(({ error }) => {
      if (error) showToast('云端删除失败，请稍后重试');
    });
  }, [currentUser, showToast]);

  const sendSignInLink = useCallback(async (email: string) => {
    if (!supabase) throw new Error('Supabase 尚未配置');
    const { error } = await supabase.auth.signInWithOtp({ email, options: { emailRedirectTo: window.location.origin } });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    if (supabase) await supabase.auth.signOut();
    setCurrentUser(null);
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const todayTasks = tasks.filter((task) => task.dueDate === today && !task.completed);
  const upcomingTasks = tasks.filter((task) => task.dueDate > today && !task.completed);
  const completedTasks = tasks.filter((task) => task.completed);
  const todayImportantTasks = todayTasks.filter((task) => task.priority === '高');
  const completedTodayCount = tasks.filter((task) => task.completed && task.dueDate === today).length;

  return <AppContext.Provider value={{
    tasks, projects, toggleTask, addTask, editTask, postponeTask, deleteTask,
    todayTasks, upcomingTasks, todayImportantTasks, completedTasks, completedTodayCount,
    darkMode, toggleDarkMode, shareModalOpen, shareModalTarget, openShareModal, closeShareModal,
    toastMessage, showToast, clearToast, currentUser, cloudSyncEnabled: isSupabaseConfigured && Boolean(currentUser),
    sendSignInLink, signOut,
  }}>{children}</AppContext.Provider>;
}

// This hook intentionally shares the provider's context contract.
// eslint-disable-next-line react-refresh/only-export-components
export function useAppState() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppState must be inside AppProvider');
  return ctx;
}
